/**
 * Vignettes de photos et visionneuse DANS la page (R4, R5, lot L1).
 *
 * POURQUOI PAS UN LIEN. La photo d'etiquette etait un `<a href="data:..." target="_blank">`.
 * Chrome refuse d'ouvrir une image `data:` dans un nouvel onglet : David restait sur un
 * onglet bloque « Chargement en cours » (recette du 26/10). La photo s'ouvre donc dans une
 * fenetre de dialogue de la page, sans jamais quitter la fiche.
 *
 * Chaque vignette est un `<button>` (souris ET clavier), et sa legende dit la date
 * d'AJOUT (`formaterDateAjout`, heure de Paris). Fermer rend le focus a la vignette :
 * sans lui, un utilisateur au clavier repartirait du haut de la page.
 *
 * Partage par les photos d'etiquettes et les photos de l'installation : une seule
 * visionneuse, un seul comportement.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { formaterDateAjout } from '@/features/distributeur/dates';

function VisionneusePhoto({ photo, onFermer }) {
  const boutonFermer = useRef(null);

  useEffect(() => {
    boutonFermer.current?.focus();
    const surTouche = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [onFermer]);

  return (
    // Le fond EST le dialogue : un clic a cote de l'image (cible = le fond lui-meme)
    // ferme, un clic sur l'image ou la legende ne ferme pas.
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.libelle}
      className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4 gap-3"
      onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}
      data-testid="visionneuse-photo"
    >
      {/* `dvh` suit la barre d'adresse mobile (iOS Safari) ; `vh` reste le repli des
          navigateurs qui ne la connaissent pas. */}
      <img src={photo.src} alt={photo.libelle}
           className="max-h-[75vh] supports-[height:100dvh]:max-h-[75dvh] max-w-full object-contain rounded-md bg-white" />
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-white text-[13px] text-center">
        <span className="font-medium">{photo.libelle}</span>
        <span className="text-white/70">{formaterDateAjout(photo.taken_at)}</span>
        <button ref={boutonFermer} type="button" onClick={onFermer}
                className="min-h-[44px] px-4 rounded-md bg-white/15 hover:bg-white/25">
          Fermer
        </button>
      </div>
    </div>
  );
}

/**
 * Une grille de vignettes. `photos` : `[{ src, libelle, taken_at, testid? }]`.
 */
export default function GaleriePhotos({ photos }) {
  const [ouverte, setOuverte] = useState(null);
  const vignettes = useRef([]);
  const rangOuvert = useRef(null);

  const ouvrir = (rang) => { rangOuvert.current = rang; setOuverte(rang); };

  // STABLE (tour 2) : `fermer` est une dependance de l'effet de la visionneuse. Recree a
  // chaque rendu, il relancait l'effet a tout rendu du parent, et l'effet redonnait le
  // focus a « Fermer » : le focus clavier sautait. Le rang est lu dans une ref.
  const fermer = useCallback(() => {
    const rang = rangOuvert.current;
    rangOuvert.current = null;
    setOuverte(null);
    vignettes.current[rang]?.focus();
  }, []);

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {photos.map((p, rang) => (
          <figure key={`${rang}-${p.src.slice(-16)}`} className="m-0">
            <button
              type="button"
              ref={(el) => { vignettes.current[rang] = el; }}
              onClick={() => ouvrir(rang)}
              aria-label={`Agrandir la photo : ${p.libelle}`}
              className="block w-full group rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
              data-testid={p.testid}
            >
              <img src={p.src} alt={p.libelle}
                   className="w-full h-24 rounded-md object-cover border border-neutral-200 group-hover:border-neutral-400" />
            </button>
            <figcaption className="mt-1 text-[10px] leading-tight">
              <span className="block text-neutral-600 truncate">{p.libelle}</span>
              <span className="block text-neutral-400">{formaterDateAjout(p.taken_at)}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      {ouverte !== null && photos[ouverte] && (
        <VisionneusePhoto photo={photos[ouverte]} onFermer={fermer} />
      )}
    </>
  );
}
